// src/hooks/useDeliveryCard.ts

import { useMutation } from "@apollo/client";
import { useState, useCallback } from "react";

import { ADD_ITEM_TO_ORDER } from "../../../mutations/AddItemToOrder/AddItemToOrder";
import type {
  AddItemToOrderResponse,
  AddItemToOrderVariables,
} from "../../../mutations/AddItemToOrder/AddItemToOrder.types";
import { AddToCartNormalized } from "../DeliveryCard/DeliveryCard.types";
import { normalizeResponse } from "../DeliveryCard/DeliveryCard.helpers";

export const useDeliveryCard = (options?: { refetch?: boolean }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const shouldRefetch = options?.refetch ?? true;

  const [mutateAddItem, { loading }] = useMutation<
    AddItemToOrderResponse,
    AddItemToOrderVariables
  >(ADD_ITEM_TO_ORDER, {
    awaitRefetchQueries: shouldRefetch,
    refetchQueries: shouldRefetch ? ["GetTotalLines", "GetActiveOrder"] : [],
  });

  // Action exposed to UI components
  const addItemToOrder = useCallback(
    async (
      productVariantId: string,
      quantity: number
    ): Promise<AddToCartNormalized> => {
      try {
        const res = await mutateAddItem({
          variables: { productVariantId, quantity },
        });

        if (!res.data) {
          return {
            ok: false,
            type: "unknown",
            message: "No response received from server.",
          };
        }

        return normalizeResponse(res.data.addItemToOrder);
      } catch (e: any) {
        return {
          ok: false,
          type: "unknown",
          message: e?.message ?? "Unexpected network error",
        };
      }
    },
    [mutateAddItem]
  );

  return {
    addItemToOrder,
    loading,
    snackbarOpen,
    setSnackbarOpen,
  };
};
